import CageColumn from '@/components/Cages/Show/CageColumn';
import { toNumericId } from '@/helpers';
import { _chunk, _isNil, _notNil } from '@/littledash';
import type { ID } from '@/model/Common.model';
import type { Study } from '@/model/Study.model';
import { errorToast } from '@/helpers';
import { useCallback, useMemo, useState } from 'react';
import { DragDropContext, type DragStart, type DropResult } from '@hello-pangea/dnd';
import { createSelector } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { featuresSelector } from '@/support/Selectors';
import { getItemIndexFromChunk } from '../../CardColumn/CardColumn.utils';
import { buildCageCards } from './CageColumns.utils';
import { ApiService } from '@/support/ApiService';
import type { Animal } from '@/model/Animal.model';
import type { CageCardCollection } from './Cages';

const reasonForChangeEnabledSelector = createSelector(
  [featuresSelector],
  (features) => features?.reason_for_change ?? false
);

interface CageCardGroup {
  id: ID;
  title: string;
  duplicateName: boolean;
  items: Array<Animal>;
}

interface CageColumnsProps {
  study: Study;
  cages: Array<CageCardCollection>;
  toggleAddAnimalsModal: (cage: CageCardCollection) => void;
  toggleDeleteCageModal: (collection: CageCardCollection) => void;
  onAddMove: (dragIndex: string, groupIndex: number, itemIndex: number) => void;
  updateCageAPI: (cage: CageCardCollection) => void;
  validateCageName: (name: string) => boolean;
  onCageNameChange: (cage: CageCardCollection, value: string) => void;
  animalIdProperty: ID;
  fetchCages: () => void;
  inStudyView: boolean;
}

const Row = ({ children }: { children: React.ReactNode }) => <div className={'cf ph2-ns'}>{children}</div>;
const Col = ({ children }: { children: React.ReactNode }) => <div className={'fl w-25 pa2'}>{children}</div>;

const CageColumns = ({
  study,
  cages = [],
  toggleAddAnimalsModal,
  toggleDeleteCageModal,
  onAddMove,
  updateCageAPI,
  validateCageName,
  onCageNameChange,
  animalIdProperty,
  fetchCages,
  inStudyView,
}: CageColumnsProps) => {
  const MAX_COLUMNS_PER_ROW = 4;
  const [dragSourceId, setDragSourceId] = useState<string | null>(null);
  const reasonForChangeEnabled = useSelector(reasonForChangeEnabledSelector);

  const groups = useMemo<Array<CageCardGroup>>(() => buildCageCards(cages), [cages]);

  const handleDragStart = (start: DragStart) => {
    setDragSourceId(start.source.droppableId);
  };

  const getCage = useCallback((id: ID) => cages.find((c) => c.id === id), [cages]);

  const rows = _chunk(groups, MAX_COLUMNS_PER_ROW);

  const updateCageName = (cage: CageCardCollection, value: string) => {
    if (value !== '') {
      cage.name = value;
      updateCageAPI(cage);
      onCageNameChange(cage, value);
    }
  };

  const handleDragEnd = (result: DropResult) => {
    const { source, destination } = result;

    if (_isNil(destination)) {
      setDragSourceId(null);
      return;
    }

    if (source.droppableId === destination.droppableId) {
      setDragSourceId(null);
      return;
    }

    const sourceGroupIdx = parseInt(source.droppableId, 10);
    const destGroupIdx = parseInt(destination.droppableId, 10);

    const sourceGroup = groups[sourceGroupIdx];
    if (_isNil(sourceGroup)) {
      setDragSourceId(null);
      return;
    }

    const animalId = parseInt(result.draggableId, 10);
    const dataIndex = sourceGroup.items.findIndex((item) => _notNil(item) && item.id === animalId);
    const animal = sourceGroup.items[dataIndex];

    if (_isNil(animal) || dataIndex === -1) {
      setDragSourceId(null);
      return;
    }

    const destGroup = groups[destGroupIdx];
    if (_isNil(destGroup)) {
      setDragSourceId(null);
      return;
    }

    const sourceIndex = `${sourceGroupIdx}-${dataIndex}`;

    onAddMove(sourceIndex, destGroupIdx, destination.index);
    setDragSourceId(null);

    ApiService.call({
      endpoint: 'PATCH /api/v1/animals',
      body: {
        animals: [{ id: toNumericId(animal.id), cage_id: toNumericId(destGroup.id) }],
        ...(reasonForChangeEnabled
          ? { reason_for_change: 'Animal cage updated via drag and drop in study cages' }
          : {}),
      },
      options: { onError: { throw: true, toast: false, slug: 'cage-columns-move-animal' } },
    }).catch(() => {
      fetchCages();
      errorToast('Unable to move animal. If this keeps occurring, please contact support');
    });
  };
  return (
    <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div className="ph3">
        {rows.map((row, rowIndex) => (
          <Row key={rowIndex}>
            {row.map((group, i) => {
              const groupIndex = getItemIndexFromChunk(i, rowIndex, MAX_COLUMNS_PER_ROW);
              const cage = getCage(group.id);

              if (_isNil(cage)) {
                return null;
              }

              return (
                <Col key={group.id}>
                  <CageColumn
                    key={group.id}
                    group={group}
                    groupId={group.id}
                    title={group.title}
                    study={study}
                    cage={cage}
                    cages={cages}
                    updateCageName={updateCageName}
                    validateCageName={validateCageName}
                    groupIndex={groupIndex}
                    toggleAddAnimalsModal={toggleAddAnimalsModal}
                    toggleDeleteCageModal={toggleDeleteCageModal}
                    animalIdProperty={animalIdProperty}
                    fetchCages={fetchCages}
                    inStudyView={inStudyView}
                    dragSourceId={dragSourceId}
                  />
                </Col>
              );
            })}
          </Row>
        ))}
      </div>
    </DragDropContext>
  );
};

export default CageColumns;
